/**
 * 权限管理 · 用户管理（canvas page-87 对齐）
 * 列表：StatRow 4 卡 + 过滤 tabs + 按角色/部门 + 7 列（姓名/邮箱/角色/部门/状态/最后登录/编辑）
 * 编辑 Drawer：姓名/员工编号/邮箱/手机/角色(单选卡片·影响所有 page UI)/部门/数据范围/重置密码
 * USERDEL-1 · 编辑态加「停用 / 启用账号」「删除账号」（页面内二次确认；后端护栏：不能对自己 / 最后一个老板或技术管理员下手 → 409 提示原样显示）
 * 模型：每用户 1 个角色 + 部门 + 数据范围（canvas 真源 · 非主+附加+监管）
 * v1 简化：同业 B2B 用户合并=Phase2 · 在线/请假态来自考勤系统(此处用 last_login 推导在线/离线) · CSV 导入占位
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2, Copy, KeyRound, Pencil, Plus, Search, ShieldAlert, ShieldCheck, Trash2, Upload, Users, UserCheck, UserPlus, UserX, X,
} from "lucide-react";
import { ApiError } from "@/services/api";
import { rbacApi, type RbacRole, type RbacUser } from "@/services/rbac";
import { ROLE_LABEL, toBackendRole, toFrontendRole, type Role } from "@/services/types/auth";
import { PageShell, Pill, LoadingState, ErrorState } from "@/shared/ui/PageHeader";
import { RbacTabs } from "./RbacTabs";

const ROLE_META: Record<Role, { emoji: string; desc: string }> = {
  tech_admin: { emoji: "🛡", desc: "系统超管 · 全平台 · 最高权限" },
  owner: { emoji: "👑", desc: "全权限 · Danger PIN" },
  op_manager: { emoji: "📊", desc: "团队管理 + 财务只读" },
  op_supervisor: { emoji: "👔", desc: "监管 OP + 审批" },
  finance: { emoji: "💰", desc: "财务 + 对账 + 开票" },
  account_manager: { emoji: "🤝", desc: "同业 B2B 客户对接" },
  op: { emoji: "👤", desc: "日常下单 + 调度" },
};
const ROLE_ORDER: Role[] = ["tech_admin", "owner", "op_manager", "op_supervisor", "finance", "account_manager", "op"];
const DEPARTMENTS = ["总裁办", "OP 部门", "财务部", "人事部", "技术部", "客户成功部", "B2B 客户"];
const DATA_SCOPES = ["全部数据", "本部门", "仅本人负责", "指定区域"];

function relTime(iso: string | null): string {
  if (!iso) return "从未登录";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "刚刚";
  if (m < 60) return `${m} 分钟前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} 小时前`;
  const d = Math.floor(h / 24);
  if (d === 1) return "昨天";
  if (d < 7) return `${d} 天前`;
  return new Date(iso).toLocaleDateString("zh-CN");
}
// 0125 · 在线 = 30 分钟内有活动（access token 15 分钟一换，活跃用户至少每 15 分钟 refresh 一次）
const isOnline = (iso: string | null) => !!iso && Date.now() - new Date(iso).getTime() < 30 * 60000;
function statusOf(u: RbacUser): { label: string; tone: "success" | "info" | "warn" | "muted" } {
  if (!u.active) return { label: "已停用", tone: "muted" };
  if (!u.last_login_at) return { label: "待激活", tone: "warn" };
  return isOnline(u.last_seen_at ?? u.last_login_at) ? { label: "在职 · 在线", tone: "success" } : { label: "在职 · 离线", tone: "info" };
}
/** 内置角色用前端别名表；自定义角色（custom_xxxxxx）用后端清单里的名字。code 一律是后端真码。 */
function makeRoleDir(roles: RbacRole[]) {
  const byCode = new Map(roles.map((r) => [r.code, r]));
  return {
    label: (code: string) => byCode.get(code)?.label_cn ?? ROLE_LABEL[toFrontendRole(code)] ?? code,
    emoji: (code: string) => (code.startsWith("custom_") ? "🧩" : (ROLE_META[toFrontendRole(code)]?.emoji ?? "👤")),
  };
}
type RoleDir = ReturnType<typeof makeRoleDir>;
/** 可分配的内置角色（后端真码；司导不在员工体系里）*/
const BUILTIN_ASSIGNABLE = ROLE_ORDER.map((r) => toBackendRole(r));

type TabKey = "all" | "staff" | "b2b" | "active" | "pending" | "disabled";

export function UsersPage() {
  const qc = useQueryClient();
  const usersQ = useQuery({ queryKey: ["rbac", "users"], queryFn: rbacApi.listUsers });
  const rolesQ = useQuery({ queryKey: ["rbac", "roles"], queryFn: rbacApi.listRoles });
  const roles = useMemo(() => rolesQ.data ?? [], [rolesQ.data]);
  const customRoles = useMemo(() => roles.filter((r) => r.kind === "custom"), [roles]);
  const dir = useMemo(() => makeRoleDir(roles), [roles]);
  const [tab, setTab] = useState<TabKey>("all");
  const [roleFilter, setRoleFilter] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [editing, setEditing] = useState<RbacUser | null>(null);
  const [creating, setCreating] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);   // USERDEL-1 · 停用 / 启用 / 删除成功提示

  const users = usersQ.data ?? [];
  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter((u) => u.active && u.last_login_at).length,
    pending: users.filter((u) => u.active && !u.last_login_at).length,
    disabled: users.filter((u) => !u.active).length,
  }), [users]);

  const depts = useMemo(
    () => Array.from(new Set(users.map((u) => u.department).filter(Boolean))) as string[],
    [users],
  );

  const filtered = useMemo(() => {
    return users
      .filter((u) => {
        // 🔴 u.primary_role 是**后端真码**（customer_manager），不是前端别名
        //（account_manager）——直接比会永远不相等，同业 B2B tab 恒为 0。过一层归一化。
        if (tab === "staff") return toFrontendRole(u.primary_role) !== "account_manager";
        if (tab === "b2b") return toFrontendRole(u.primary_role) === "account_manager";
        if (tab === "active") return u.active && u.last_login_at;
        if (tab === "pending") return u.active && !u.last_login_at;
        if (tab === "disabled") return !u.active;
        return true;
      })
      .filter((u) => !roleFilter || u.primary_role === roleFilter)
      .filter((u) => !deptFilter || u.department === deptFilter)
      .sort((a, b) => (b.last_login_at ?? "").localeCompare(a.last_login_at ?? ""));
  }, [users, tab, roleFilter, deptFilter]);

  if (usersQ.isLoading) return <LoadingState />;
  if (usersQ.isError) return <ErrorState message="用户列表加载失败" onRetry={() => usersQ.refetch()} />;

  const TABS: { key: TabKey; label: string; n: number }[] = [
    { key: "all", label: "全部", n: stats.total },
    { key: "staff", label: "员工", n: users.filter((u) => toFrontendRole(u.primary_role) !== "account_manager").length },
    { key: "b2b", label: "同业 B2B", n: users.filter((u) => toFrontendRole(u.primary_role) === "account_manager").length },
    { key: "active", label: "🟢 活跃", n: stats.active },
    { key: "pending", label: "🟡 待激活", n: stats.pending },
    { key: "disabled", label: "⚪ 已停用", n: stats.disabled },
  ];

  return (
    <PageShell
      title="用户管理"
      subtitle="主角色 + 附加角色（权限取合计）· 部门 · 数据范围"
      actions={
        <div className="flex items-center gap-2">
          <button
            type="button"
            title="批量导入 CSV（待接）"
            className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-[13px] text-slate-400"
          >
            <Upload className="h-3.5 w-3.5" /> 批量导入 CSV
          </button>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white"
          >
            <UserPlus className="h-3.5 w-3.5" /> 邀请新员工
          </button>
        </div>
      }
    >
      <RbacTabs />

      {flash && (
        <div role="status" className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700">
          <CheckCircle2 className="h-4 w-4" /> {flash}
          <button type="button" onClick={() => setFlash(null)} className="ml-auto text-emerald-500 hover:text-emerald-700">关闭</button>
        </div>
      )}

      {/* StatRow */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={<Users className="h-4 w-4" />} tone="slate" label="总用户数" value={stats.total} hint="员工 + 同业 B2B" />
        <StatCard icon={<UserCheck className="h-4 w-4" />} tone="emerald" label="在职活跃" value={stats.active} hint="登录过" />
        <StatCard icon={<UserPlus className="h-4 w-4" />} tone="amber" label="待激活" value={stats.pending} hint="从未登录 · 系统不发邀请邮件，需管理员重置密码后线下转交" />
        <StatCard icon={<UserX className="h-4 w-4" />} tone="rose" label="已停用" value={stats.disabled} hint="禁止登录" />
      </div>

      {/* 过滤 tabs + 按角色/部门 */}
      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`rounded-lg border px-3 py-1.5 text-[12px] font-medium ${tab === t.key ? "border-blue-300 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
          >
            {t.label} ({t.n})
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="h-8 rounded-lg border border-slate-200 bg-white pl-7 pr-2 text-[12px]"
            >
              <option value="">按角色过滤</option>
              {BUILTIN_ASSIGNABLE.map((c) => <option key={c} value={c}>{dir.label(c)}</option>)}
              {customRoles.map((r) => <option key={r.code} value={r.code}>{r.label_cn}（自定义）</option>)}
            </select>
          </div>
          <select
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
            className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[12px]"
          >
            <option value="">按部门过滤</option>
            {depts.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      {/* 用户表 */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">工号</th>
              <th className="px-4 py-3 text-left">姓名</th>
              <th className="px-4 py-3 text-left">邮箱</th>
              <th className="px-4 py-3 text-left">角色</th>
              <th className="px-4 py-3 text-left">部门</th>
              <th className="px-4 py-3 text-left">状态</th>
              <th className="px-4 py-3 text-left">最后登录</th>
              <th className="px-4 py-3 text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const st = statusOf(u);
              return (
                <tr
                  key={u.id}
                  className="cursor-pointer border-t border-slate-100 hover:bg-slate-50/70"
                  onClick={() => setEditing(u)}
                >
                  <td className="px-4 py-3 font-mono text-[12px] font-semibold text-slate-900" data-testid={`emp-no-${u.id}`}>{u.employee_no ?? "—"}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                  <td className="px-4 py-3 font-mono text-[12px] text-slate-500">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[12px] text-slate-700">
                      {dir.emoji(u.primary_role)} {dir.label(u.primary_role)}
                    </span>
                    {u.extra_roles.length > 0 && (
                      <span className="ml-1 text-[11px] text-slate-400" title={u.extra_roles.map((c) => dir.label(c)).join("、")}>+{u.extra_roles.length}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{u.department ?? "—"}</td>
                  <td className="px-4 py-3"><Pill tone={st.tone === "warn" ? "muted" : st.tone}>{st.label}</Pill></td>
                  <td className="px-4 py-3 text-[12px] text-slate-500">{relTime(u.last_login_at)}</td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => setEditing(u)}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-50"
                    >
                      <Pencil className="h-3 w-3" /> 编辑
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[13px] text-slate-400">无匹配用户</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">显示 {filtered.length} / {stats.total} · 按上次登录倒序</p>

      {(editing || creating) && (
        <UserDrawer
          user={editing}
          roles={roles}
          dir={dir}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={(msg?: string) => {
            setEditing(null); setCreating(false);
            if (msg) setFlash(msg);
            qc.invalidateQueries({ queryKey: ["rbac", "users"] });
          }}
        />
      )}
    </PageShell>
  );
}

function StatCard({ icon, tone, label, value, hint }: { icon: React.ReactNode; tone: "slate" | "emerald" | "amber" | "rose"; label: string; value: number; hint: string }) {
  const toneCls: Record<string, string> = {
    slate: "bg-slate-100 text-slate-600",
    emerald: "bg-emerald-100 text-emerald-600",
    amber: "bg-amber-100 text-amber-600",
    rose: "bg-rose-100 text-rose-600",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${toneCls[tone]}`}>{icon}</span>
        <span className="text-[12px] text-slate-500">{label}</span>
      </div>
      <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-[11px] text-slate-400">{hint}</p>
    </div>
  );
}

/** 编辑 / 邀请用户 Drawer（右侧滑入） */
function UserDrawer({ user, roles, dir, onClose, onSaved }: { user: RbacUser | null; roles: RbacRole[]; dir: RoleDir; onClose: () => void; onSaved: (msg?: string) => void }) {
  const customRoles = roles.filter((r) => r.kind === "custom");
  const isEdit = !!user;
  const [f, setF] = useState({
    name: user?.name ?? "",
    email: user?.email ?? "",
    phone: user?.phone ?? "",
    // 后端真码（op_assistant 而不是前端别名 op）；提交时 toBackendRolePayload 对真码原样放行
    primary_role: user?.primary_role ?? "op_assistant",
    extra_roles: user?.extra_roles ?? ([] as string[]),
    department: user?.department ?? "",
    data_scope: user?.data_scope ?? "全部数据",
  });
  const [newPw, setNewPw] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name: f.name,
        primary_role: f.primary_role,
        extra_roles: f.extra_roles.filter((c) => c !== f.primary_role),
        department: f.department || null,
        data_scope: f.data_scope || null,
        phone: f.phone || null,   // IDFILL-1 · 工号由系统生成、只读：不再提交 employee_no
      };
      if (isEdit) await rbacApi.updateUser(user!.id, payload);
      else await rbacApi.createUser({ email: f.email, ...payload });
    },
    onSuccess: () => onSaved(),
  });
  const reset = useMutation({
    mutationFn: () => rbacApi.resetPassword(user!.id),
    onSuccess: (r) => setNewPw(r.password),
  });
  const resetMfa = useMutation({
    mutationFn: () => rbacApi.resetMfa(user!.id),
    onSuccess: () => onSaved(), // 重置后 mfa_enabled 变了·drawer 直接关掉+刷新列表，不用本地再合并一次状态
  });
  // USERDEL-1 · 停用 / 启用 / 删除：先页面内确认，确认后才调接口；失败（如 409 护栏）在确认框里显示后端原话
  const [confirming, setConfirming] = useState<"toggle" | "delete" | null>(null);
  const lifecycle = useMutation({
    mutationFn: async (kind: "toggle" | "delete") => {
      if (kind === "delete") await rbacApi.deleteUser(user!.id);
      else await rbacApi.updateUser(user!.id, { active: !user!.active });
    },
    onSuccess: (_r, kind) => {
      setConfirming(null);
      onSaved(kind === "delete" ? `已删除账号「${user!.name}」· 可在回收站恢复` : user!.active ? `已停用账号「${user!.name}」` : `已启用账号「${user!.name}」`);
    },
  });
  const lifecycleErr = lifecycle.error instanceof ApiError ? lifecycle.error.message : "操作失败 · 请重试";
  const saveDetail = save.error instanceof ApiError ? (save.error.detail as { detail?: unknown } | undefined)?.detail : undefined;
  const saveErr = typeof saveDetail === "string" && saveDetail ? saveDetail : "保存失败 · 请检查邮箱是否重复";

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="h-full w-full max-w-md overflow-y-auto bg-white shadow-xl animate-in slide-in-from-right"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-start justify-between border-b border-slate-100 bg-white px-6 py-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900">{isEdit ? `编辑用户 · ${user!.name}` : "邀请新员工"}</h3>
            {isEdit && <p className="mt-0.5 font-mono text-[12px] text-slate-400">{user!.email}</p>}
          </div>
          <button type="button" onClick={onClose}><X className="h-4 w-4 text-slate-400 hover:text-slate-600" /></button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <div className="grid grid-cols-2 gap-3">
            <DField label="姓名 *"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} /></DField>
            <DField label="工号">
              <input value={isEdit ? (user!.employee_no ?? "—") : "保存后自动生成"} disabled readOnly data-testid="emp-no-field" className={`${inputCls} bg-slate-50 font-mono text-slate-500`} />
            </DField>
            <DField label="邮箱 *">
              <input value={f.email} disabled={isEdit} onChange={(e) => setF({ ...f, email: e.target.value })} className={`${inputCls} ${isEdit ? "bg-slate-50 text-slate-400" : ""}`} />
            </DField>
            <DField label="手机"><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+1 801-555-0000" className={inputCls} /></DField>
          </div>

          <div>
            <p className="mb-1.5 text-[12px] font-medium text-slate-700">主角色 *<span className="ml-1 text-[11px] font-normal text-slate-400">影响所有 page UI</span></p>
            <select
              data-testid="primary-role"
              value={f.primary_role}
              onChange={(e) => setF({ ...f, primary_role: e.target.value, extra_roles: f.extra_roles.filter((c) => c !== e.target.value) })}
              className={inputCls}
            >
              <optgroup label="内置角色">
                {BUILTIN_ASSIGNABLE.map((c) => <option key={c} value={c}>{dir.emoji(c)} {dir.label(c)}</option>)}
              </optgroup>
              {customRoles.length > 0 && (
                <optgroup label="自定义角色">
                  {customRoles.map((r) => <option key={r.code} value={r.code}>{dir.emoji(r.code)} {r.label_cn} · {r.permission_codes.length} 权限点</option>)}
                </optgroup>
              )}
            </select>
            <p className="mb-1.5 mt-3 text-[12px] font-medium text-slate-700">附加角色<span className="ml-1 text-[11px] font-normal text-slate-400">可多选，权限取合计</span></p>
            <div data-testid="extra-roles" className="flex flex-wrap gap-1.5">
              {[...BUILTIN_ASSIGNABLE, ...customRoles.map((r) => r.code)]
                .filter((c) => c !== f.primary_role)
                .map((c) => {
                  const on = f.extra_roles.includes(c);
                  return (
                    <label key={c} className={`flex cursor-pointer items-center gap-1 rounded-full border px-2.5 py-1 text-[12px] ${on ? "border-blue-300 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600"}`}>
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={on}
                        onChange={() => setF({ ...f, extra_roles: on ? f.extra_roles.filter((x) => x !== c) : [...f.extra_roles, c] })}
                      />
                      {dir.emoji(c)} {dir.label(c)}
                    </label>
                  );
                })}
            </div>
            {f.extra_roles.length === 0 && <p className="mt-1 text-[11px] text-slate-400">不加</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <DField label="部门">
              <select value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} className={inputCls}>
                <option value="">未设置</option>
                {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </DField>
            <DField label="数据范围">
              <select value={f.data_scope} onChange={(e) => setF({ ...f, data_scope: e.target.value })} className={inputCls}>
                {DATA_SCOPES.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </DField>
          </div>

          {isEdit && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-[13px] font-medium text-slate-700"><KeyRound className="h-3.5 w-3.5" /> 重置密码</span>
                <button
                  type="button"
                  onClick={() => reset.mutate()}
                  disabled={reset.isPending}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1 text-[12px] hover:bg-slate-100 disabled:opacity-50"
                >
                  {reset.isPending ? "生成中…" : "生成新密码"}
                </button>
              </div>
              {newPw && (
                <div className="mt-2 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                  <code className="flex-1 font-mono text-[13px] text-slate-800">{newPw}</code>
                  <button type="button" onClick={() => navigator.clipboard.writeText(newPw)} title="复制"><Copy className="h-3.5 w-3.5 text-slate-500" /></button>
                </div>
              )}
              {newPw && <p className="mt-1 text-[11px] text-amber-700">⚠ 请立即复制并转交 · 关闭后无法再查看</p>}
            </div>
          )}

          {/* MFAFIX-1 Part 3 · 换手机/丢手机/删错验证器 App 后唯一的自助出口——
              没有这个按钮，同事只能等 SSH 进服务器手工清库（工单原话："死锁换个形式回来"）。*/}
          {isEdit && user!.mfa_required && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-[13px] font-medium text-slate-700">
                  {user!.mfa_enabled ? (
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                  ) : (
                    <ShieldAlert className="h-3.5 w-3.5 text-amber-600" />
                  )}
                  双重验证（MFA）
                  <span className="text-[11px] font-normal text-slate-400">
                    {user!.mfa_enabled ? "已绑定" : "未绑定 · 下次登录会要求先绑定"}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`确认重置 ${user!.name} 的 MFA？重置后该账号下次登录需重新扫码绑定。`)) {
                      resetMfa.mutate();
                    }
                  }}
                  disabled={resetMfa.isPending || !user!.mfa_enabled}
                  title={user!.mfa_enabled ? "" : "还没绑定过·无需重置"}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1 text-[12px] hover:bg-slate-100 disabled:opacity-50"
                >
                  {resetMfa.isPending ? "重置中…" : "重置 MFA"}
                </button>
              </div>
              {resetMfa.isError && <p className="mt-1 text-[11px] text-rose-600">重置失败 · 请重试</p>}
            </div>
          )}

          {/* USERDEL-1 · 账号状态 + 危险区（编辑态才有） */}
          {isEdit && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-[13px] font-medium text-slate-700">
                  {user!.active ? <UserCheck className="h-3.5 w-3.5 text-emerald-600" /> : <UserX className="h-3.5 w-3.5 text-rose-600" />}
                  账号状态
                  <span className="text-[11px] font-normal text-slate-400">{user!.active ? "正常 · 可登录" : "已停用 · 不能登录"}</span>
                </span>
                <button
                  type="button"
                  onClick={() => { lifecycle.reset(); setConfirming("toggle"); }}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1 text-[12px] hover:bg-slate-100"
                >
                  {user!.active ? "停用账号" : "启用账号"}
                </button>
              </div>
            </div>
          )}
          {isEdit && (
            <div className="rounded-lg border border-rose-200 bg-rose-50/50 p-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-[13px] font-medium text-rose-700"><Trash2 className="h-3.5 w-3.5" /> 危险操作</span>
                <button
                  type="button"
                  onClick={() => { lifecycle.reset(); setConfirming("delete"); }}
                  className="rounded-md border border-rose-300 bg-white px-3 py-1 text-[12px] text-rose-600 hover:bg-rose-50"
                >
                  删除账号
                </button>
              </div>
              <p className="mt-1 text-[11px] text-rose-500">删除后账号从列表移除，可在回收站恢复</p>
            </div>
          )}

          {/* USERDEL-1 · 后端给了原话（如 409「不能把最后一个有效的技术管理员账号改成其它主角色」）就显示原话；拿不到才用通用提示 */}
          {save.isError && <p role="alert" className="text-[12px] text-rose-600">{saveErr}</p>}
        </div>

        <div className="sticky bottom-0 flex justify-end gap-2 border-t border-slate-100 bg-white px-6 py-4">
          <button type="button" onClick={onClose} className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-[13px]">取消</button>
          <button
            type="button"
            disabled={!f.name || (!isEdit && !f.email) || save.isPending}
            onClick={() => save.mutate()}
            className="h-9 rounded-lg bg-blue-600 px-5 text-[13px] font-semibold text-white disabled:opacity-50"
          >
            {save.isPending ? "保存中…" : isEdit ? "保存" : "创建并邀请"}
          </button>
        </div>
      </div>

      {confirming && isEdit && (
        <ConfirmDialog
          title={confirming === "delete" ? `删除账号 · ${user!.name}` : `${user!.active ? "停用" : "启用"}账号 · ${user!.name}`}
          body={
            confirming === "delete"
              ? "删除后账号从列表移除，可在回收站恢复。"
              : user!.active
                ? "停用后该账号不能登录，记录保留，可随时启用。"
                : "启用后该账号可以重新登录。"
          }
          confirmLabel={confirming === "delete" ? "确认删除" : user!.active ? "确认停用" : "确认启用"}
          danger={confirming === "delete" || user!.active}
          pending={lifecycle.isPending}
          error={lifecycle.isError ? lifecycleErr : null}
          onCancel={() => setConfirming(null)}
          onConfirm={() => lifecycle.mutate(confirming)}
        />
      )}
    </div>
  );
}

/** USERDEL-1 · 页面内二次确认（同 RolesPage ModalShell 的动效 / 结构） */
function ConfirmDialog({ title, body, confirmLabel, danger, pending, error, onCancel, onConfirm }: {
  title: string; body: string; confirmLabel: string; danger: boolean; pending: boolean; error: string | null; onCancel: () => void; onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm" onClick={(e) => { e.stopPropagation(); onCancel(); }}>
      <div
        role="dialog"
        aria-label={title}
        className="flex w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <button type="button" aria-label="关闭" onClick={onCancel}><X className="h-4 w-4 text-slate-400 hover:text-slate-600" /></button>
        </div>
        <div className="space-y-3 px-6 py-5">
          <p className="text-[13px] text-slate-600">{body}</p>
          {error && <p role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <button type="button" onClick={onCancel} className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-[13px]">取消</button>
          <button
            type="button"
            disabled={pending}
            onClick={onConfirm}
            className={`h-9 rounded-lg px-5 text-[13px] font-semibold text-white disabled:opacity-50 ${danger ? "bg-rose-600" : "bg-blue-600"}`}
          >
            {pending ? "处理中…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px]";
function DField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[12px] font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}
